import React, {ReactElement} from 'react';
import {useTranslation} from 'react-i18next';
import {IValidationErrorObject} from '../../../../../incase-domain/common/validationErrorResponse';
import Button from '../../../../../incase-domain/components/button/Button';
import {IElementImageGallery} from '../../../../../incase-domain/elements/imageGallery';
import NumberInput from '../../../../../ui/components/NumberInput';
import {generateErrorMessages, getErrorClasses} from '../../../../../utils/validation';
import SectionTitle from '../../../../elements/common/SectionTitle';
import useImgFileReader from '../../../../fileLoader/util/useImgFileReader';
import {TABS} from '../../../form/part/tabs';
import AnimationForm from '../../common/animationForm';
import ElementShadow from '../../common/elementShadow';
import HighlightedInputMedia from '../../../../fileLoader/input.media';
import ElementImageGalleryEditComponent from './imageGalleryEdit';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../../utils/hooks/slideUpdates/useSetSlideElement';

interface HighlightedImageGalleryElementComponentProps {
    errors: IValidationErrorObject | null;
    activeTab: string;
}

function HighlightedImageGalleryElementComponent(props: HighlightedImageGalleryElementComponentProps): ReactElement {
    const {errors, activeTab} = props;
    const {t} = useTranslation();
    const updateHighlightedElement = useSetHighlightedElement();
    const highlightedElement = useHighlightedElement() as IElementImageGallery;

    const onFileLoad = useImgFileReader((images) => {
        updateHighlightedElement.single('images', [...highlightedElement.images, ...images]);
    });

    if (activeTab === TABS.STYLE.id) {
        return <ElementShadow highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />;
    }

    if (activeTab === TABS.ADVANCED.id) {
        return (
            <>
                <AnimationForm
                    appearAfter={highlightedElement.appearAfter || 0}
                    elementAnimations={highlightedElement.elementAnimations}
                    highlightedElementChangeHandler={updateHighlightedElement}
                />
            </>
        );
    }

    return (
        <>
            <div className={`l-group--form l-group__item ${getErrorClasses(errors, 'images')}`}>
                <SectionTitle
                    label={t('designSidepanel.images')}
                    leading={
                        <HighlightedInputMedia
                            type="image"
                            child={
                                <Button
                                    className="c-sequenceQuestionElementOrder__shuffleButton"
                                    size="2xs"
                                    leading="ph:plus"
                                    variant="ghost"
                                    color="primary"
                                >
                                    {t('designSidepanel.new')}
                                </Button>
                            }
                            multipleFiles
                            onLoad={onFileLoad}
                        />
                    }
                />
                {generateErrorMessages(errors, 'images')}
            </div>
            <ElementImageGalleryEditComponent element={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />
            <hr style={{marginBottom: '0rem'}} />
            <div className="l-group--form">
                <SectionTitle label={t('designSidepanel.layout')} />
                <NumberInput
                    label={t('designSidepanel.imagesInARow')}
                    step={1}
                    min={1}
                    onChange={(newValue) => updateHighlightedElement.single('numberOfImagesInARow', newValue)}
                    value={highlightedElement.numberOfImagesInARow || 1}
                />
                <div className="l-group__item">
                    <NumberInput
                        label={t('designSidepanel.imageMargin')}
                        step={0.1}
                        min={0}
                        onChange={(newValue) => updateHighlightedElement.single('imageMargin', newValue)}
                        value={highlightedElement.imageMargin || 0}
                    />
                </div>
            </div>
        </>
    );
}

export default HighlightedImageGalleryElementComponent;
